import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getRatingSpecifications,
  createRatingSpecification,
  updateRatingSpecification,
  deleteRatingSpecification,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

const RatingSpecificationManagementTable = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filter, setFilter] = useState("");
  const [nameError, setNameError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [ratingSpecToDelete, setRatingSpecToDelete] = useState(null);
  const [ratingSpecs, setRatingSpecs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [ratingSpecForm, setRatingSpecForm] = useState({
    name: "",
    created_by: localStorage.getItem("user_id"),
  });

  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [multiRatingSpecs, setMultiRatingSpecs] = useState([{ name: "" }]);

  // ── Theme-aware helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  // ── Fetch ──
  const fetchRatingSpecs = async (
    reset = false,
    pageNo = 1,
    search = filter
  ) => {
    try {
      const data = await getRatingSpecifications(pageNo, search);
      if (reset) {
        setRatingSpecs(data.results);
      } else {
        setRatingSpecs((prev) => [...prev, ...data.results]);
      }
      setHasMore(!!data.next);
      setPage(pageNo + 1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch Rating Specifications");
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchRatingSpecs(true, 1, filter);
    }, 500);
    return () => clearTimeout(handler);
  }, [filter]);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  // ── Save (Add / Edit) ──
  const handleSave = async (nameParam) => {
    const createdBy = localStorage.getItem("user_id");
    const finalName = nameParam || ratingSpecForm.name;

    if (!finalName.trim()) {
      setNameError("Rating Specification name is required");
      return;
    }
    setNameError("");

    try {
      if (editId) {
        const update_res = await updateRatingSpecification(editId, {
          name: finalName,
        });
        setRatingSpecs(
          ratingSpecs.map((rs) =>
            rs.id === editId ? { ...rs, ...update_res } : rs
          )
        );
        toast.success("Rating Specification updated successfully");
      } else {
        const response = await createRatingSpecification({
          name: finalName,
          created_by: createdBy,
        });
        toast.success("Rating Specification added successfully");
        if (Array.isArray(response)) {
          setRatingSpecs([...response, ...ratingSpecs]);
        } else {
          setRatingSpecs([response, ...ratingSpecs]);
        }
      }
      setRatingSpecForm({ name: "", created_by: createdBy });
      setEditId(null);
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Failed to save";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
      if (msg.toString().toLowerCase().includes("already exists")) {
        setNameError("Rating Specification with this name already exists.");
      }
    }
  };

  const handleSubmitAll = async () => {
    const createdBy = localStorage.getItem("user_id");
    const validSpecs = multiRatingSpecs
      .filter((c) => c.name.trim() !== "")
      .map((c) => ({
        name: c.name.trim(),
        created_by: createdBy,
      }));

    if (validSpecs.length === 0) {
      setNameError("Please enter at least one Rating Specification");
      return;
    }
    try {
      const response = await createRatingSpecification(validSpecs);
      toast.success("Rating Specifications added successfully");
      if (Array.isArray(response)) {
        setRatingSpecs([...response, ...ratingSpecs]);
      } else {
        setRatingSpecs([response, ...ratingSpecs]);
      }
      setMultiRatingSpecs([{ name: "" }]);
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.[0]?.name?.[0] ||
        err?.response?.data?.name?.[0] ||
        "Failed to save";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    }
  };

  const handleEdit = (rs) => {
    setRatingSpecForm({ name: rs.name });
    setEditId(rs.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteRatingSpecification(id);
      setRatingSpecs((prev) => prev.filter((rs) => rs.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAddField = () => {
    if (multiRatingSpecs.length >= 5) {
      toast.error("You can add maximum 5 Rating Specifications");
      return;
    }
    setMultiRatingSpecs([...multiRatingSpecs, { name: "" }]);
  };

  const handleRemoveField = (index) => {
    setMultiRatingSpecs(multiRatingSpecs.filter((_, i) => i !== index));
  };

  const handleChange = (index, value) => {
    const newFields = [...multiRatingSpecs];
    newFields[index].name = value;
    setMultiRatingSpecs(newFields);
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  const closeDialog = () => {
    setOpen(false);
    setEditId(null);
    setRatingSpecForm({ name: "" });
    setMultiRatingSpecs([{ name: "" }]);
    setNameError("");
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100%" }}>
      {/* ── Search + Add Bar ── */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        sx={{
          mt: { xs: 3, md: 1 },
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          px: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        {/* Search */}
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <TextField
            label="Search by Rating Specification"
            variant="outlined"
            size="small"
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="e.g. 500 KVA"
            sx={{
              "& .MuiInputBase-root": {
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": { fontSize: "0.7rem" },
            }}
          />
        </Box>

        {/* Add Button */}
        <Box sx={{ ml: "15px" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: "30px",
              fontSize: "0.7rem",
              padding: "4px 10px",
              borderRadius: "0px",
            }}
            onClick={() => {
              setEditId(null);
              setRatingSpecForm({ name: "" });
              setMultiRatingSpecs([{ name: "" }]);
              setNameError("");
              setOpen(true);
            }}
          >
            Rating Specification
          </Button>
        </Box>
      </Box>

      {/* ── Table / Cards ── */}
      <div id="project-table-print">
        <InfiniteScroll
          dataLength={ratingSpecs.length}
          next={() => fetchRatingSpecs(false, page, filter)}
          hasMore={hasMore}
          loader={
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "text.secondary",
                }}
              >
                Loading...
              </Typography>
            </Box>
          }
          endMessage={
            <Typography
              sx={{
                textAlign: "center",
                py: 2,
                fontSize: { xs: "0.85rem", sm: "1rem" },
                color: "text.disabled",
              }}
            >
              No more rating specifications
            </Typography>
          }
        >
          {isMobile ? (
            /* ── MOBILE CARDS ── */
            <Box sx={{ bgcolor: "background.paper" }}>
              {ratingSpecs.map((rs, index) => (
                <Box
                  key={rs.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 1.5,
                    bgcolor: rowBg(index),
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    transition: "background-color 0.15s ease",
                    "&:active": { bgcolor: "action.selected" },
                  }}
                >
                  {/* Left Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          color: theme.palette.text.disabled,
                          fontSize: "0.75rem",
                        }}
                      >
                        {index + 1}
                      </span>{" "}
                      {rs.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        ml: 1,
                      }}
                    >
                      {formatOnlyDate(rs.created_at)}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    {/* Edit */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(rs);
                        setOpen(true);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.15)
                            : "#e3f2fd",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.primary.main, 0.28)
                              : "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon
                        sx={{ fontSize: "0.95rem", color: "primary.main" }}
                      />
                    </IconButton>

                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setRatingSpecToDelete(rs.id);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.28)
                              : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon
                        sx={{ fontSize: "0.95rem", color: "error.main" }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: "88vh",
                overflowX: "auto",
                overflowY: "auto",
                width: "100%",
                bgcolor: "background.paper",
                backgroundImage: "none",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      color: "text.primary",
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: tableHeadBg,
                      fontSize: "0.75rem",
                      letterSpacing: "0.04em",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: tableHeadBg,
                        minWidth: 50,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell sx={{ backgroundColor: tableHeadBg }}>
                      Rating Specification
                    </TableCell>
                    <TableCell sx={{ backgroundColor: tableHeadBg }}>
                      Date
                    </TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: tableHeadBg,
                        zIndex: 3,
                        minWidth: 80,
                        padding: 0,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {ratingSpecs.map((rs, index) => (
                    <TableRow
                      hover
                      key={rs.id || index}
                      sx={{
                        backgroundColor: rowBg(index),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: (t) =>
                            t.palette.mode === "dark"
                              ? `${alpha(
                                  t.palette.primary.main,
                                  0.1
                                )} !important`
                              : "#e3f2fd !important",
                          "& td": {
                            backgroundColor: "inherit !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: "text.primary",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: "inherit",
                          zIndex: 1,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{rs.name}</TableCell>
                      <TableCell>{formatOnlyDate(rs.created_at)}</TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          bgcolor: "inherit",
                          zIndex: 1,
                          minWidth: 80,
                        }}
                      >
                        <IconButton
                          color="primary"
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          size="small"
                          onClick={() => {
                            handleEdit(rs);
                            setOpen(true);
                          }}
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          color="error"
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setRatingSpecToDelete(rs.id);
                          }}
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </div>

      {/* ══════════════════════════════════════════════ */}
      {/* ADD / EDIT DIALOG                             */}
      {/* ══════════════════════════════════════════════ */}
      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          {editId ? "Edit Rating Specification" : "Add Rating Specification"}
        </DialogTitle>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (editId) {
              handleSave();
            } else {
              handleSubmitAll();
            }
          }}
        >
          <DialogContent dividers sx={{ bgcolor: "background.paper" }}>
            {editId ? (
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="Rating Specification"
                  placeholder="e.g. 500 KVA, 3 Phase"
                  fullWidth
                  value={ratingSpecForm.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRatingSpecForm((prev) => ({ ...prev, name: value }));
                    if (value.trim()) setNameError("");
                  }}
                  error={!!nameError}
                  helperText={nameError}
                  required
                />
              </Box>
            ) : (
              <>
                {multiRatingSpecs.map((rs, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      label={`Rating Specification ${index + 1}`}
                      placeholder="e.g. 500 KVA, 3 Phase"
                      fullWidth
                      value={rs.name}
                      onChange={(e) => handleChange(index, e.target.value)}
                      required={index === 0}
                    />
                    <Box sx={{ width: 40, textAlign: "center" }}>
                      {multiRatingSpecs.length > 1 && (
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveField(index)}
                          sx={{
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.error.main, 0.15)
                                  : "#ffebee",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
                {nameError && (
                  <Typography color="error" variant="caption">
                    {nameError}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>

          {/* ADD ROW row */}
          <DialogActions
            sx={{
              justifyContent: "flex-start",
              mb: -5,
              bgcolor: "background.paper",
            }}
          >
            {!editId && (
              <Button
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={handleAddField}
                sx={{
                  height: 35,
                  fontSize: 14,
                  padding: "2px 10px",
                  minWidth: "auto",
                  textTransform: "none",
                  border: "none",
                  display: "flex",
                  bgcolor: "transparent",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.primary.main, 0.1)
                        : alpha(t.palette.primary.main, 0.06),
                  },
                }}
              >
                ADD ROW
              </Button>
            )}
          </DialogActions>

          {/* Footer Actions */}
          <DialogActions
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1.2,
              bgcolor: "background.paper",
            }}
          >
            {/* Close */}
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={closeDialog}
            >
              CLOSE
            </Button>

            {/* Save (Add mode) */}
            {!editId && (
              <Button
                type="submit"
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: "0.85rem",
                  padding: "4px 12px",
                }}
              >
                SAVE
              </Button>
            )}

            {/* Update (Edit mode) */}
            {editId && (
              <Button type="submit" variant="contained" size="small">
                Update
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setRatingSpecToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.primary", mb: 1 }}
          >
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setRatingSpecToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                try {
                  await handleDelete(ratingSpecToDelete);
                } finally {
                  setAnchorEl(null);
                  setRatingSpecToDelete(null);
                }
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />
    </Box>
  );
};

export default RatingSpecificationManagementTable;